import React, { useMemo } from 'react';
import ArrowDownwardIcon from '@mui/icons-material//ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material//ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material//ArrowUpward';
import InfoOutlinedIcon from '@mui/icons-material//InfoOutlined';
import { Box, Tooltip, Typography } from '@mui/material';
import { METRIC_NOT_AVAILABLE, VALUE_PROPERTY } from 'common/constants/dashboards';
import { formatMD } from 'dashboard409a/utilities';
import { MetricCardProps } from './MetricCard.props';
import { classes, StyledBox } from './MetricCard.styles';

const CHANGE_PROPERTY = 'change' as const;

const MetricCard = ({ title, value, format }: MetricCardProps) => {
  const changeData = useMemo(() => {
    // null (no data) renders as N/A with the neutral arrow.
    const change = value.change ?? 0;
    const isUp = change > 0;
    const isDown = change < 0;

    let changeTextClass = 'neutral';
    if (isUp) {
      changeTextClass = 'up';
    } else if (isDown) {
      changeTextClass = 'down';
    }

    return {
      isUp,
      isDown,
      changeTextClass,
    };
  }, [value.change]);

  const renderArrow = () => {
    if (changeData.isDown) {
      return <ArrowDownwardIcon className={`${classes.arrowIcon} down`} />;
    }
    if (changeData.isUp) {
      return <ArrowUpwardIcon className={`${classes.arrowIcon} up`} />;
    }
    return <ArrowForwardIcon className={`${classes.arrowIcon} neutral`} />;
  };

  const changeSuffix = format === '%' ? ' points' : '%';

  const formatDisplay = (
    value: { value?: number | null; change?: number | null },
    property: typeof VALUE_PROPERTY | typeof CHANGE_PROPERTY,
    format: string
  ): string => {
    const val = value[property];
    if (val === undefined || val === null) {
      return METRIC_NOT_AVAILABLE;
    }

    return `${val.toFixed(2)}${format}`;
  };

  const displayValue = formatDisplay(value, VALUE_PROPERTY, format);
  const displayChange = formatDisplay(value, CHANGE_PROPERTY, changeSuffix);

  // Rolling ~30-day comparison, not a calendar month — show the actual dates.
  const dateRangeLabel =
    value.current_date && value.previous_date
      ? `${formatMD({ date: value.previous_date })} vs ${formatMD({ date: value.current_date })}`
      : null;

  return (
    <StyledBox className={classes.cardContainer}>
      <Typography className={classes.subtitle}>Median</Typography>
      <Typography variant="body1" className={classes.title}>
        {title}
      </Typography>
      <Box className={classes.valueContainer}>
        <Typography className={classes.value}>{displayValue}</Typography>

        {value && (
          <Box className={classes.changeValueContainer}>
            {renderArrow()}
            <Typography className={`${classes.changeText} ${changeData.changeTextClass}`}>
              {displayChange} month over month
            </Typography>
            {dateRangeLabel && (
              <Tooltip arrow title={`Comparing ${dateRangeLabel}`}>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </StyledBox>
  );
};

export default MetricCard;
